import { useEffect, useRef, useState } from 'react';
import type { AuthConfiguration, GoogleStart } from '../shared/auth';
import { api, errorMessage } from './api';

export function useAuthConfiguration() {
  const [config, setConfig] = useState<AuthConfiguration | null>(null);
  const [error, setError] = useState('');
  const [retry, setRetry] = useState(0);
  const serial = useRef(0);
  useEffect(() => {
    const request = ++serial.current;
    setError('');
    void api<AuthConfiguration>('/auth/config')
      .then((value) => {
        if (request === serial.current) setConfig(value);
      })
      .catch((error) => {
        if (request === serial.current) setError(errorMessage(error));
      });
    return () => {
      serial.current++;
    };
  }, [retry]);
  return { config, error, retry: () => setRetry((previous) => previous + 1) };
}
export async function startGoogle(request: GoogleStart, isCurrent: () => boolean) {
  const result = await api<{ authorizationUrl: string }>('/auth/google/start', 'POST', request);
  if (!isCurrent()) return;
  const target = new URL(result.authorizationUrl);
  if (target.origin !== 'https://accounts.google.com' || target.username || target.password)
    throw new Error('Google sign-in returned an invalid destination. Try again.');
  window.location.assign(target.href);
}
export function googleCompletionMessage(code: string | null) {
  const messages: Record<string, string> = {
    google_unavailable:
      'Google sign-in is unavailable. Try again later or use another enabled sign-in method.',
    invalid_state: 'This Google sign-in attempt expired or could not be verified. Start again.',
    google_denied: 'Google sign-in was cancelled or denied. You can try again.',
    invalid_identity: 'Use your approved Google Workspace account to continue.',
    account_unavailable: 'This account is unavailable. Contact your administrator.',
    invite_invalid: 'This invitation is no longer valid. Ask your administrator for a new link.',
    link_required: 'Sign in with your password first, then link Google from your profile.',
    link_conflict:
      'This Google account cannot be linked to this profile. Contact your administrator.',
    session_expired: 'Your session expired. Sign in again before linking Google.',
  };
  return code && Object.hasOwn(messages, code)
    ? messages[code]
    : 'Google sign-in could not be completed. Start again.';
}
